import * as React from 'react';
import { compose, setDisplayName, lifecycle, withState, withHandlers, withProps } from 'recompose';
import { translate } from 'react-i18next';
import { TranslateProps } from 'util/i18n';
import { connect } from 'react-redux';
import { State } from 'reducers';
import { modal } from 'ui/shared/modal';
import { ReportType, ReportId } from 'types';
import { ErrorMessage } from 'ui/shared/ErrorMessage';
import { Fragment } from 'react';
import { Button } from 'ui/shared/Button';
import { Input } from 'ui/shared/Input';
import { getReportNameTextKey, reportTypeSorter } from 'logic/reports/reportType';
import { getReportTypes } from 'selectors/serverSelectors';

import './NewReportDialog.css';

type ReportTypeFieldProps = {
    reportType: ReportType;
    checked: boolean;
    labelKey: string;
    onChange: React.ChangeEventHandler<HTMLInputElement>;
};

const ReportTypeField = ({ reportType, labelKey, checked, onChange, t }: ReportTypeFieldProps & TranslateProps) =>
    <label>
        <input
            type="radio"
            id={ `new-report-type-${reportType}` }
            name="reportType"
            value={ reportType }
            onChange={ onChange }
            checked={ checked }
        />
        { t(labelKey) }
    </label>
;


export type FormValues = {
    reportId: ReportId | null;
    name: string;
    reportType: ReportType;
    shared: boolean;
};
export type OwnProps = {
    reportId: ReportId | null;
    initialName: string;
    initialReportType: ReportType;
    initialShared: boolean;
    visible: boolean;
    onAccept: (values: FormValues) => void;
    onCancel: () => void;
};
type StateProps = {
    reportTypes: ReportType[];
};
type RecomposeProps = FormValues & {
    nameError: string | null;
    handleChangeName: React.ChangeEventHandler<HTMLInputElement>;
    handleChangeReportType: React.ChangeEventHandler<HTMLInputElement>;
    handleChangeShared: React.ChangeEventHandler<HTMLInputElement>;
    handleSubmit: React.FormEventHandler<HTMLFormElement>;
    handleCancel: React.MouseEventHandler<HTMLButtonElement>;
};
type _RecomposeProps = RecomposeProps & {
    _setName: (n: string) => void;
    _setNameError: (n: string | null) => void;
    _setReportType: (r: ReportType) => void;
    _setShared: (s: boolean) => void;
};

type Props = OwnProps & StateProps & RecomposeProps & TranslateProps;

const styles: { [name: string]: React.CSSProperties } = {
    alignTop: {
        verticalAlign: 'top',
    },
    buttonBar: {
        marginTop: '1em',
        float: 'right',
    },
};

export const RawNewReportDialog = ({
    name,
    nameError,
    reportType,
    reportTypes,
    shared,
    handleChangeName,
    handleChangeReportType,
    handleChangeShared,
    handleSubmit,
    handleCancel,
    t,
}: Props) =>
    <Fragment>
        <h2>
            { t('6.1241.0.8') }
        </h2>
        <form
            onSubmit={ handleSubmit }
        >
            <table>
                <tbody>
                    <tr>
                        <td style={ styles.alignTop }>
                            <label htmlFor="new-report-name">
                                { t('6.1241.0.22') }
                            </label>
                        </td>
                        <td>
                            <Input
                                id="new-report-name"
                                name="name"
                                value={ name }
                                onChange={ handleChangeName }
                                autoComplete={ 'none' }
                                autoFocus={ true }
                            />
                            { nameError &&
                                <ErrorMessage text={ nameError }/>
                            }
                        </td>
                    </tr>
                    <tr>
                        <td style={ styles.alignTop }>
                            <label htmlFor="new-report-type">
                                { t('6.1241.0.23') }
                            </label>
                        </td>
                        <td>
                            { reportTypes.map(rt =>
                                <ReportTypeField
                                    key={ rt }
                                    reportType={ rt }
                                    labelKey={ getReportNameTextKey(rt) }
                                    checked={ reportType === rt }
                                    onChange={ handleChangeReportType }
                                    t={ t }
                                />
                            ) }
                        </td>
                    </tr>
                    <tr>
                        <td style={ styles.alignTop }>
                            <label htmlFor="new-report-shared">
                                { t('6.1241.0.25') }
                            </label>
                        </td>
                        <td>
                            <label>
                                <input
                                    type="checkbox"
                                    id="new-report-shared"
                                    name="shared"
                                    onChange={ handleChangeShared }
                                    checked={ shared }
                                />
                                { t('6.1241.0.26') }
                            </label>
                        </td>
                    </tr>
                </tbody>
            </table>
            <div style={ styles.buttonBar }>
                <Button type="submit" disabled={ !!nameError } textKey="1"/>
                <Button type="button" onClick={ handleCancel } textKey="290"/>
            </div>
        </form>
    </Fragment>
;

const mapStateToProps = (state: State) => ({
    reportTypes: getReportTypes(state),
});

export const NewReportDialog = compose<Props, OwnProps>(
    setDisplayName('NewReportDialog'),
    translate(),
    connect<StateProps, {}, OwnProps>(mapStateToProps),
    withProps<Partial<Props>, Props>(({ reportTypes, t }) => ({
        reportTypes: reportTypes.sort(reportTypeSorter(t)),
    })),
    withState('name', '_setName', ({ initialName }) => initialName),
    withState('reportType', '_setReportType', ({ initialReportType }) => initialReportType),
    withState('shared', '_setShared', ({ initialShared }) => initialShared),
    withState('nameError', '_setNameError', ''),
    withHandlers({
        handleChangeName: ({ _setName, _setNameError, t }) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const name = e.currentTarget.value;
            _setName(name);

            if (name.trim().length === 0) {
                _setNameError(t('2360')); // Name Required
            }
            else {
                _setNameError(null);
            }
        },
        handleChangeReportType: ({ _setReportType }) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const reportType = e.currentTarget.value;
            _setReportType(reportType);
        },
        handleChangeShared: ({ _setShared }) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const shared = e.currentTarget.checked;
            _setShared(shared);
        },
        handleSubmit: ({ reportId, name, reportType, shared, onAccept }) => (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            onAccept({
                reportId,
                name: name.trim(),
                reportType,
                shared,
            });
        },
        handleCancel: ({ onCancel }) => (e: React.MouseEvent<HTMLButtonElement>) => {
            onCancel();
        },
    }),
    lifecycle<Props & _RecomposeProps, {}>({
        componentDidUpdate(oldProps: Props) {
            const { visible, initialName, initialReportType, initialShared, _setName, _setReportType, _setShared } = this.props;
            if (visible && !oldProps.visible) {
                _setName(initialName);
                _setReportType(initialReportType);
                _setShared(initialShared);
            }
        },
    }),
    modal('new-report-dialog'),
)(RawNewReportDialog);



// WEBPACK FOOTER //
// ./src/ui/pages/reports/NewReportDialog.tsx